<template>
    <div
        v-if="siteReport"
        class="col-sm-12 editor-form nopads w-100 px-2"
    >
        <div
            class="col-12 nopads"
        >
            <h3 class="editor-header">
                {{ $t('contract_diary.site_report') }}
            </h3>
            <div class="row nopads">
                <div class="col-sm-6 pl-1 pr-1">
                    <span class="span-title">{{ $t('contract_diary.date') }}</span>
                    <input
                        id="record_date"
                        class="mt-1 form-control form-control-sm"
                        :value="siteReport.record_date ? timestampToDateInput(new Date(siteReport.record_date).getTime()) : null"
                        :max="today"
                        type="date"
                        @change="updateRecordDate"
                    >
                </div>
                <div class="col-sm-6 pl-1 pr-1">
                    <span class="span-title">{{ $t('contract_diary.end_date') }}</span>
                    <input
                        id="record_date"
                        class="mt-1 form-control form-control-sm"
                        :value="siteReport.record_end_date ? timestampToDateInput(new Date(siteReport.record_end_date).getTime()) : null"
                        type="date"
                        @change="updateRecordEndDate"
                    >
                </div>
                <div class="col-sm-6 pl-1 pr-1 mt-2" style="margin-top: 2px">
                    <open-contract-filter
                        ref="contracts"
                        :contract="contract"
                        :from="now"
                        :show-title="true"
                        :show-company="false"
                        :apply-padding="false"
                        @contract-selected="setContract"
                    />
                </div>
            </div>
            <div
                class="row nopads"
            >
                <div
                    class="col-sm-12 nopads pl-1 pr-1"
                    style="padding-top: .2em"
                >
                    <span class="span-title">{{ $t('contract_diary.supervisors') }}</span>
                    <input
                        id="supervisors"
                        v-model="siteReport.supervisors"
                        class="no-margin form-control form-control-sm"
                        type="text"
                    >
                </div>
            </div>
            <div class="row">
                <div class="col-sm-6 pr-1 pl-1">
                    <span class="span-title">{{ $t('contract_diary.events') }}</span>
                    <textarea
                        id="events"
                        v-model="siteReport.events"
                        class="form-control form-control-sm col-12"
                        rows="7"
                    />
                    <span class="tip-light">
                        {{ siteReport.events ? siteReport.events.length : 0 }} / 500
                    </span>
                </div>
                <div class="col-sm-6 pr-1 pl-1">
                    <span class="span-title">{{ $t('contract_diary.deviations') }}</span>
                    <textarea
                        id="deviations"
                        v-model="siteReport.deviations"
                        class="form-control form-control-sm col-12"
                        rows="7"
                    />
                    <span class="tip-light">
                        {{ siteReport.deviations ? siteReport.deviations.length : 0 }} / 500
                    </span>
                </div>
            </div>
            <div
                class="col-12"
            >
                <!-- PHOTOS -->
                <div
                    v-if="siteReport.photos && siteReport.photos.length >= 1"
                    class="row nopads text-center my-2"
                >
                    <Flicking
                        v-if="siteReport.photos && siteReport.photos.length > 0"
                        ref="flicking"
                        :options="options"
                        :plugins="plugins"
                        class="pointer nopads text-center"
                        style="height: 15em;"
                    >
                        <ImageView
                            v-for="photo in siteReport.photos"
                            :key="photo.id"
                            ref="image"
                            class="flicking-panel mx-1"
                            :contain="true"
                            :url="getPhotoUrl(contractWeeklyDiaryPhotoUrl, photo)"
                            :control-size="`sm`"
                            @click="openImage"
                        />
                        <template #viewport>
                            <div
                                class="flicking-pagination flicking-pagination-bullets-container"
                                style="margin-top: 2em"
                            />
                        </template>
                    </Flicking>
                </div>
            </div>
        </div>
        <div
            class="col-12 pl-1 pr-1"
        >
            <!-- Photo editor-->
            <div class="col-12">
                <!-- Show selected photos -->
                <div
                    v-for="(file, index) in photos"
                    :key="index"
                    class="d-flex align-items-center justify-content-between mb-3"
                >
                    <span>{{ file?.name || file }}</span>
                    <button
                        class="btn btn-outline-danger "
                        type="button"
                        @click="removePhoto(index)"
                    >
                        {{ $t('common.remove') }}
                    </button>
                </div>

                <!-- Hidden file input -->
                <input
                    ref="photoInput"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    class="visually-hidden"
                    @change="handlePhotoChange"
                >
            </div>
        </div>
        <!-- Fullscreen photo -->
        <Transition name="fade">
            <fullscreen-image
                v-if="fullScreenPhotoData"
                :large-images="fullScreenPhotoData"
                :panel-index="panelIndex"
                @close="closePhoto"
            />
        </Transition>
        <div
            class="col-sm-12 my-2 text-end"
        >
            <button
                class="btn btn-outline-primary form-button"
                type="button"
                @click="triggerPhotoInput"
            >
                {{ $t('power_stations.add_photo') }}
            </button>

            <button
                class="form-button btn btn-secondary"
                @click.stop="$emit('close')"
            >
                {{ $t('common.cancel') }}
            </button>
            <button
                class="form-button btn btn-success"
                :disabled="loading"
                @click.stop="submitContractsiteReport"
            >
                <span v-if="siteReport.id > 0">
                    {{ $t('contract_diary.save_contract_weekly_report') }}
                </span>
                <span v-else>
                    {{ $t('contract_diary.add_contract_weekly_report') }}
                </span>
            </button>
        </div>
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {workManagementHelper} from '../mixins/WorkManagementMixin'
import {userHelper} from "@/components/mixins/UserMixin";
import {timeUtils} from "@/components/mixins/TimeUtils";
import {mapHelper} from "../mixins/MapMixin";
import OpenContractFilter from "@/components/contract/OpenContractFilter.vue";
import {attachmentHelper} from "@/components/mixins/AttachmentMixin";
import ImageView from "@/components/view/ImageView.vue";
import "@egjs/flicking-plugins/dist/arrow.css";
import "@egjs/vue3-flicking/dist/flicking.css";
import {Pagination} from "@egjs/flicking-plugins";
import Flicking from "@egjs/vue3-flicking";
import FullscreenImage from "@/components/view/FullscreenImage.vue";
import ContractWorkDiary from "@/components/contract/external/ContractWorkDiary";
import DateTimePicker from "@/components/common/DateTimePicker";
import {inject} from "vue";

export default {
    name: 'ContractWeekyDiaryEditor',
    components: {FullscreenImage, ImageView, OpenContractFilter, Flicking, ContractWorkDiary, DateTimePicker},
    mixins: [workManagementHelper, restApi, userHelper, mapHelper, timeUtils, attachmentHelper],
    props: {
        modelValue: {
            type: Object,
            default: null
        },
        reportListView: {
            type: Boolean,
            default: false
        },
        preSelectedContract: {
            type: Object,
            default: null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            siteReport: null,
            options: {
                preventDefaultOnDrag: true,
                circular: false,
                defaultIndex: 1,
                panelsPerView: 3
            },
            photos: [],
            fullScreenPhotoData: null,
            plugins: [new Pagination({ type: 'bullet' })],
            contract: null,
            users: [],
            panelIndex: 0,
        }
    },
    computed: {
        today: function() {
            const today = new Date();
            return today.toISOString().split('T')[0];
        },

        now() {
            return new Date().toISOString();
        },
    },
    created() {
        this.fetchUsers()
        this.siteReport = this.modelValue
        this.contract = this.siteReport.contract ? this.siteReport.contract : this.preSelectedContract
    },
    methods: {
        triggerPhotoInput() {
            this.$refs.photoInput?.click();
        },
        handlePhotoChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.photos.push(file);
            }
            // Clear input value so the same file can be selected again if needed
            event.target.value = '';
        },
        removePhoto(index) {
            this.photos.splice(index, 1);
        },

        convertLocalToUTC(dateString) {
            const localDate = new Date(dateString);
            const utcOffset = localDate.getTimezoneOffset() * 60000; // Offset in milliseconds
            const utcDate = new Date(localDate.getTime() - utcOffset);

            return utcDate.toISOString(); // ISO string format for backend
        },
        setDefaultValues: function (value) {
            let siteReport = JSON.parse(JSON.stringify(value))
            if(this.contract) {
                siteReport.contract = this.contract
            } else {
                this.showAlert(this.$t('contract_diary.invalid_contract'))
            }
            if(this.siteReport.record_date) {
                siteReport.record_date = this.convertLocalToUTC(this.siteReport.record_date);
            }
            return siteReport
        },

        submitContractsiteReport: function () {
            let data = this.setDefaultValues(this.siteReport)
            if (data !== undefined) {
                this.loading = true
                if (this.siteReport.id < 1) {
                    this.restAdd(this.contractWeeklyDiaryUrl, data, this.success, this.fail)
                } else {
                    this.restUpdate(this.contractWeeklyDiaryUrl, data, this.success, this.fail)
                }
            }
        },

        success: function (response) {
            var photos = this.getValidAttachments(this.photos)
            if (photos && photos.length > 0 && response) {
                this.uploadPhotos(photos, response)
            } else {
                this.loading = false
                this.showAlert(this.$t('common.alert_update_success'), 'success')
                this.$emit('closeAndUpdate')

            }
        },

        fail() {
            this.loading = false
            this.showAlert(this.$t('common.alert_update_failed'))
        },

        setContract: function (contract) {
            this.contract = contract
        },
        updateRecordDate: function (event) {
            if (event) {
                this.siteReport.record_date = new Date(event.target.value)
            } else {
                this.siteReport.record_date = undefined
            }
        },
        updateRecordEndDate: function (event) {
            if (event) {
                this.siteReport.record_end_date = new Date(event.target.value)
            } else {
                this.siteReport.record_end_date = undefined
            }
        },
        getPhotoUrl: function (baseUrl, photo) {
            return baseUrl + '/' + this.siteReport.id + '/' + photo.id
        },
        openImage: function () {
            const flicking = this.$refs.flicking;
            const imageData = this.$refs.image;

            // Reset the full-screen photo data array
            this.fullScreenPhotoData = [];

            if (Array.isArray(imageData)) {
                // Handle multiple images
                imageData.forEach(src => {
                    this.fullScreenPhotoData.push(src);
                });
            } else if (imageData) {
                // Handle a single image (else block case)
                this.fullScreenPhotoData.push(imageData);
            }

            // Update the panel index
            if (flicking) {
                this.panelIndex = flicking.index;
            }
        },
        closePhoto() {
            this.fullScreenPhotoData = null
        },
        uploadPhotos: function (photos, response) {
            if (photos && photos.length > 0) {
                this.loading = true
                let formData = new FormData()
                formData.append('diary_id', response.id)
                photos.forEach(photoFile => {
                    if (photoFile !== undefined && photoFile !== null) {
                        formData.append('photos[]', photoFile)
                    }
                })
                this.restPostWithFile(this.contractWeeklyDiaryPhotoUrl, {}, formData, this.photoSuccess, this.fail)
            }
        },
        photoSuccess: function () {
            this.loading = false
            this.showAlert(this.$t('common.alert_update_success'), 'success')
            this.$emit('closeAndUpdate')

        },
        fetchUsers: function () {
            this.loading = true;
            this.users = []
            this.restFetchParams(this.userCompanyUrl, {active: 1}, this.handleUserResponse)
        },
        handleUserResponse: function (response) {
            if (response && response.data) {
                response.data.forEach(function (user) {
                    this.users.push({
                        last_name: user.last_name,
                        first_name: user.first_name,
                        id: user.id
                    })
                }, this)
            }
            this.loading = false
        },
    }
}
</script>
<style scoped>
.image-container {
    width: fit-content;
}
</style>
